import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { Collapsible } from "@/shared/ui/Collapsible";
import { formatDateTime, formatMoney } from "@/shared/format";
import { fetchOrderReturns, type AddressForm, type ReturnRecord } from "@/features/resi/api";
import { EmettiEtichettaDialog } from "@/features/resi/components/EmettiEtichettaDialog";
import { KindBadge, StatusBadge } from "@/features/resi/components/PraticaBadge";
import { openReturnBordero, openReturnLabel } from "@/features/resi/labelDownload";
import type { Order, SupplierReplacementEntry } from "../../types";
import { RiordinaSostButton, supplierReplacements } from "../Sostituzioni";
import { RientroMagazzinoButton, puoRientrare } from "../RientroMagazzino";
import { EsitoMerceBanner } from "../EsitoMerceBanner";
import { RimborsoManualeBanner } from "../RimborsoManualeBanner";
import { BTN as BTN_PAGINA } from "./stile";

/** Pratica ancora viva. `chiuso`/`annullato` sono storia, il resto e' lavoro. */
function eAperta(r: ReturnRecord): boolean {
  return r.status !== "chiuso" && r.status !== "annullato";
}

/** Il destinatario per esteso: su una sostituzione e' spesso un indirizzo
 *  TERZO (la figlia, l'ufficio) e finora non si vedeva da nessuna parte — si
 *  scopriva aprendo la pagina Resi, cioe' quasi mai. */
function destinatario(a: Partial<AddressForm>): string {
  const chi = a.company || [a.name, a.surname].filter(Boolean).join(" ");
  const luogo = [a.postcode, a.city].filter(Boolean).join(" ");
  const posto = luogo && a.state_iso ? `${luogo} (${a.state_iso})` : luogo;
  return [chi, a.address, posto, a.phone ? `tel ${a.phone}` : null].filter(Boolean).join(" · ");
}

// Testo nero e area di click piena (Davide 2026-09-23 e 2026-10-05): prima
// grigio da 12px, si perdeva accanto ai badge.
// Il tasto secondario e' quello di tutta la pagina (stile.ts).
const BTN = BTN_PAGINA;
// Il passo che manca ha il colore del badge «Etichetta da fare»: e' l'unico
// tasto della riga che fa avanzare la pratica.
const BTN_PRIMARIO =
  "inline-flex min-h-[38px] items-center rounded-md border border-violet bg-violet px-3 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-violet-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";

/** Una pratica reso/sostituzione (Davide 2026-09-17: «piccolo e grigio, non si
 *  capisce nulla»). Tipo e stato in badge colorati; sotto, ogni dato con la sua
 *  etichetta — prima tracking e riferimento interno stavano in una riga grigia
 *  da 10,5px separati da puntini, e non si capiva quale fosse il tracking. */
function ReturnRow({
  record,
  labelBusy,
  onLabel,
  onBordero,
  onEmetti,
}: {
  record: ReturnRecord;
  labelBusy: boolean;
  onLabel: (r: ReturnRecord) => void;
  onBordero: (r: ReturnRecord) => void;
  onEmetti: (r: ReturnRecord) => void;
}) {
  const dest = destinatario(record.recipient);
  const [tracking, ...altriColli] = record.tracking_numbers;
  return (
    <div className="border-t border-line py-3 first:border-t-0 first:pt-1">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <KindBadge kind={record.kind} />
          <StatusBadge status={record.status} />
          <span className="text-[12px] tabular-nums text-ink">
            {formatDateTime(record.created_at)}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {record.status === "richiesta" && (
            <button type="button" onClick={() => onEmetti(record)} className={BTN_PRIMARIO}>
              Crea etichetta
            </button>
          )}
          {/* ⚠️ Gate su `has_label`, NON su `label_path`: un record coi soli
              byte in `label_pdf` (migr 0035) ha l'etichetta e il percorso
              nullo — il bottone spariva su etichette che esistono. */}
          {record.has_label === true && (
            <button
              type="button"
              disabled={labelBusy}
              onClick={() => onLabel(record)}
              className={BTN}
            >
              {labelBusy ? "Apro…" : "Etichetta"}
            </button>
          )}
          {record.has_label === true && record.carrier === "BRT" && (
            <button
              type="button"
              disabled={labelBusy}
              onClick={() => onBordero(record)}
              className={BTN}
            >
              Borderò
            </button>
          )}
          <Link to="/resi" className={BTN}>
            Apri in Resi
          </Link>
        </div>
      </div>
      <dl className="mt-2 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-[13px]">
        <dt className="font-medium text-ink">Tracking</dt>
        <dd className="min-w-0">
          {!tracking ? (
            <span className="text-ink">non ancora assegnato</span>
          ) : record.tracking_url ? (
            <a
              href={record.tracking_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-ink underline decoration-dotted underline-offset-2 hover:decoration-solid"
            >
              {tracking}
            </a>
          ) : (
            <span className="font-mono text-ink">{tracking}</span>
          )}
          {altriColli.length > 0 && (
            <span className="ml-2 text-[12px] text-ink">
              +{altriColli.length} {altriColli.length === 1 ? "collo" : "colli"}
            </span>
          )}
        </dd>
        {dest && (
          <>
            <dt className="font-medium text-ink">Destinatario</dt>
            <dd className="min-w-0 text-ink">{dest}</dd>
          </>
        )}
        {record.note && (
          <>
            <dt className="font-medium text-ink">Nota</dt>
            <dd className="min-w-0 text-ink">«{record.note}»</dd>
          </>
        )}
        {record.num_rif_interno && (
          <>
            <dt className="font-medium text-ink">Rif. interno</dt>
            <dd className="min-w-0 font-mono text-[12px] text-ink">{record.num_rif_interno}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

/** Costo di una entry COME REGISTRATO, con l'etichetta che dice in quale delle
 *  due forme è: le entry da mail Ita/Ity sono NETTE, quelle manuali AllService
 *  IVATE (`gross: true`, il popup chiede l'ivato di fattura). Mostrarle senza
 *  guardare il flag falserebbe il costo.
 *
 *  ⚠️ Nessun ÷1,22 qui e nessuna somma: il netto lo fa il backend
 *  (`orders/replacements.py`, fonte unica) e il totale della sezione viene da
 *  lì — replicarlo in TS darebbe una seconda definizione dello stesso invariante.
 *
 *  `cost: null` è un caso REALE (la mail «merce pronta sost» può arrivare prima
 *  della conferma con l'importo): il totale backend la salta, qui si dice che
 *  l'importo manca — mai «€ 0,00». */
function entryCost(entry: SupplierReplacementEntry, currency?: string): string {
  if (entry.cost == null) return "Importo non ancora arrivato";
  return `${formatMoney(entry.cost, currency)} ${entry.gross === true ? "IVA incl." : "netto"}`;
}

/** Addebiti del fornitore per le sostituzioni (mail «sost» Ita/Ity e
 *  rispedizioni AS manuali): rif. o descrizione, data, importo.
 *
 *  Il totale e' quello del backend (gia' netto), mai la somma delle entry: le
 *  AS manuali sono salvate ivate. Compare solo quando aggiunge qualcosa — con
 *  una sola entry netta ripeterebbe la riga sopra. Prima stava in testa come
 *  «1 · € 0,00 addebitati» anche con l'importo non ancora arrivato. */
function AddebitiFornitore({
  replacements,
  currency,
}: {
  replacements: ReturnType<typeof supplierReplacements>;
  currency?: string;
}) {
  const { entries, count, costTotal } = replacements;
  const inAttesa = entries.filter((e) => e.cost == null).length;
  const conImporto = count - inAttesa;
  const mostraTotale = conImporto > 0 && (count > 1 || entries.some((e) => e.gross === true));
  return (
    <div className="border-t border-line pt-3 first:border-t-0 first:pt-1">
      <div className="mb-1 text-[12.5px] font-medium text-ink">Addebiti fornitore</div>
      {entries.map((entry, i) => {
        // Rif. fornitore dal writer mail, `what` da quello manuale AS: nessuno
        // dei due valorizza il campo dell'altro. `||` e non `??`: è JSONB senza
        // schema, la stringa vuota va trattata come assente al pari di null.
        const ref = entry.supplier_ref || "";
        const label = ref || entry.what || "Sostituzione";
        return (
          // Le entry non hanno un id stabile → key posizionale: lista in sola
          // lettura, mai riordinata.
          <div
            key={`${entry.at}-${i}`}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 py-1 text-[13px]"
          >
            <div className="min-w-0">
              <span className={ref ? "font-mono text-ink" : "text-ink"}>{label}</span>
              <span className="ml-2 text-[12px] tabular-nums text-ink">
                {formatDateTime(entry.at)}
              </span>
            </div>
            <span
              className={
                entry.cost == null
                  ? "shrink-0 rounded-full bg-amber-soft px-2 py-0.5 text-[12px] text-ink"
                  : "shrink-0 font-medium tabular-nums text-ink"
              }
            >
              {entryCost(entry, currency)}
            </span>
          </div>
        );
      })}
      {mostraTotale && (
        <div
          data-testid="sost-totale"
          className="mt-1 flex items-center justify-between gap-3 border-t border-line pt-1.5 text-[13px]"
        >
          <span className="text-ink">Totale addebitato</span>
          <span className="font-semibold tabular-nums text-ink">
            {formatMoney(costTotal, currency)} netto
            {inAttesa > 0 && (
              <span className="ml-1.5 text-[12px] font-normal text-ink">
                · {inAttesa} in attesa dell&apos;importo
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

/** Sostituzioni fornitore (mail "sost" Ita/Ity) + rispedizioni AllService
 *  + resi/sostituzioni SDA collegati + azioni Crea reso / Crea sostituzione.
 *
 *  A scomparsa (redesign 3 colonne, 2026-09-02) ma col conteggio nel titolo:
 *  su 4 ordini su 5 qui non c'e' niente, e una sezione vuota aperta e' spazio
 *  rubato al resto della pagina. Le AZIONI restano fuori dal collassabile. */
export function ReplacementsSection({
  order,
  onChanged,
  onCreateReturn,
  returnBusy,
}: {
  order: Order;
  onChanged: () => void;
  onCreateReturn: (ref: string, kind: "reso" | "sostituzione") => void;
  returnBusy?: boolean;
}) {
  const replacements = supplierReplacements(order);
  // Mirror volontario della formula inline di OrderCard.tsx (~riga 1481,
  // `asReplacementCount`): tenerle in sync a mano finché non viene estratta
  // in un helper condiviso (review P2-T3).
  const asCount =
    Number(order.raw?.as_replacement_count ?? 0) ||
    (typeof order.raw?.as_replacement_sent_at === "string" ? 1 : 0);

  const [returns, setReturns] = useState<ReturnRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [labelBusyId, setLabelBusyId] = useState<number | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);
  // Pratica per cui si sta emettendo l'etichetta (passo 2 della sostituzione
  // in due tempi): il dialog e' quello del modulo resi, colli + destinatario.
  const [emettiPer, setEmettiPer] = useState<ReturnRecord | null>(null);

  const orderRef = order.id;
  useEffect(() => {
    const ctrl = new AbortController();
    fetchOrderReturns(orderRef, ctrl.signal)
      .then((rows) => {
        if (ctrl.signal.aborted) return;
        setReturns(rows);
        setError(null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setError(prettifyApiError(e));
      });
    return () => ctrl.abort();
  }, [orderRef]);

  async function handleLabel(record: ReturnRecord, scarica = openReturnLabel) {
    if (labelBusyId != null) return;
    setLabelBusyId(record.id);
    setLabelError(null);
    try {
      await scarica(record);
    } catch (e) {
      setLabelError(prettifyApiError(e));
    } finally {
      setLabelBusyId(null);
    }
  }

  const aperte = returns.filter(eAperta);
  const chiuse = returns.length - aperte.length;
  const qualcosa = returns.length > 0 || replacements.count > 0 || asCount > 0;
  const tono = aperte.length > 0 ? "amber" : "neutral";
  const riassunto =
    [
      aperte.length > 0 ? `${aperte.length} ${aperte.length === 1 ? "aperta" : "aperte"}` : null,
      chiuse > 0 ? `${chiuse} ${chiuse === 1 ? "chiusa" : "chiuse"}` : null,
      replacements.count > 0 ? `${replacements.count} dal fornitore` : null,
      asCount > 0 ? `${asCount} rispedizioni AS` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "nessuno";

  return (
    <div className="mb-4">
      {puoRientrare(order) && <EsitoMerceBanner riferimento={order.id} />}
      {/* Solo ManoMano, BricoBravo e Amazon: lo decide il server (stato «da_decidere»). */}
      <RimborsoManualeBanner riferimento={order.id} />
      {/* Azioni nel piede del pannello: sempre a vista anche da chiuso (un tasto
          nascosto e' un tasto che non esiste), ma DENTRO il bordo, accanto a
          cio' su cui agiscono — prima galleggiavano sotto, staccati. */}
      <Collapsible
        footer={
          <>
            <RiordinaSostButton order={order} onDone={onChanged} />
            <button
              type="button"
              disabled={returnBusy}
              onClick={() => onCreateReturn(order.id, "reso")}
              className={BTN}
            >
              Crea reso
            </button>
            <button
              type="button"
              disabled={returnBusy}
              onClick={() => onCreateReturn(order.id, "sostituzione")}
              className={BTN}
            >
              Crea sostituzione
            </button>
            {puoRientrare(order) && <RientroMagazzinoButton order={order} variant="pagina" />}
          </>
        }
        // ⚠️ `key`: Collapsible decide tono e apertura AL MOUNT, e i resi
        // arrivano da una fetch, dopo. Senza rimontare, una pratica aperta
        // trovata dalla rete resterebbe dietro a una sezione chiusa e grigia.
        // Una chiusura esplicita sopravvive lo stesso: vive in sessionStorage.
        key={`${tono}-${String(qualcosa)}`}
        id="sostituzioni"
        title="Sostituzioni e resi"
        summary={riassunto}
        tone={tono}
        area="merce"
        defaultOpen={qualcosa}
      >
        {/* Prima le pratiche (sono il lavoro da fare), poi i costi. Una fonte
            vuota non stampa niente: tre righe «nessuna» erano rumore che
            copriva l'unica riga vera. */}
        {error && <div className="py-1 text-[12px] text-red">Resi non caricati: {error}</div>}
        {returns.map((r) => (
          <ReturnRow
            key={r.id}
            record={r}
            labelBusy={labelBusyId === r.id}
            onLabel={(rec) => void handleLabel(rec)}
            onBordero={(rec) => void handleLabel(rec, openReturnBordero)}
            onEmetti={setEmettiPer}
          />
        ))}
        {replacements.count > 0 && (
          <AddebitiFornitore replacements={replacements} currency={order.currency} />
        )}
        {asCount > 0 && (
          <div className="flex justify-between border-t border-line py-2 text-[13px] first:border-t-0">
            <span className="text-ink">Rispedizioni AllService</span>
            <span className="font-medium tabular-nums text-ink">×{asCount}</span>
          </div>
        )}
        {!qualcosa && !error && (
          <div className="py-1 text-[13px] text-ink">
            Nessun reso o sostituzione su questo ordine.
          </div>
        )}
        {labelError && (
          <div role="alert" className="mt-1 text-[12px] text-red">
            {labelError}
          </div>
        )}
      </Collapsible>
      {emettiPer && (
        <EmettiEtichettaDialog
          record={emettiPer}
          onEmessa={(aggiornato) => {
            setReturns((prev) => prev.map((r) => (r.id === aggiornato.id ? aggiornato : r)));
            setEmettiPer(null);
            onChanged();
          }}
          onClose={() => setEmettiPer(null)}
        />
      )}
    </div>
  );
}
